import {
  SubscriptionBillingMigrationReason,
  SubscriptionFeaturesLeft,
  SubscriptionPlan,
  SubscriptionProductChangeDetails,
} from "@/types/subscription";

const MS_IN_A_DAY = 24 * 60 * 60 * 1000;
const MAX_SAFE_SUBSCRIPTION_TIMESTAMP = Number.MAX_SAFE_INTEGER;
const EMPTY_FEATURES_LEFT: SubscriptionFeaturesLeft = {
  apply_request_left: 0,
  job_post_left: 0,
};

type RawSubscriptionPlan = Partial<SubscriptionPlan> & {
  features_left?: SubscriptionFeaturesLeft | null;
  featuresLeft?: SubscriptionFeaturesLeft | null;
};

export const isSubscriptionExpired = (msLeft: number | null | undefined): boolean =>
  msLeft == null || msLeft <= 0;

export const isSubscriptionBillingMigrationRequired = (
  subscription: Pick<SubscriptionPlan, "requires_billing_migration" | "has_stripe_subscription">,
): boolean => subscription.requires_billing_migration && !subscription.has_stripe_subscription;

export const getSubscriptionBillingMigrationDaysLeft = (
  billingMigrationExpiresAtMs: number | null | undefined,
): number | null => {
  if (
    typeof billingMigrationExpiresAtMs !== "number" ||
    !Number.isFinite(billingMigrationExpiresAtMs) ||
    billingMigrationExpiresAtMs <= 0
  ) {
    return null;
  }

  return Math.max(0, Math.ceil((billingMigrationExpiresAtMs - Date.now()) / MS_IN_A_DAY));
};

const isConcreteSubscriptionTimestamp = (value: number | null | undefined): value is number =>
  typeof value === "number" &&
  Number.isFinite(value) &&
  value > 0 &&
  value < MAX_SAFE_SUBSCRIPTION_TIMESTAMP;

export const getSubscriptionRenewalAtMs = (
  subscription: Pick<SubscriptionPlan, "display_renewal_at_ms" | "expiration_at_ms">,
): number | null => {
  if (isConcreteSubscriptionTimestamp(subscription.display_renewal_at_ms)) {
    return subscription.display_renewal_at_ms;
  }

  if (isConcreteSubscriptionTimestamp(subscription.expiration_at_ms)) {
    return subscription.expiration_at_ms;
  }

  return null;
};

const normalizeBillingMigrationExpiresAtMs = (
  value: number | null | undefined,
): number | null => {
  if (!isConcreteSubscriptionTimestamp(value)) {
    return null;
  }

  return value;
};

export const formatSubscriptionBillingMigrationDeadline = ({
  billingMigrationExpiresAtMs,
  locale,
  t,
}: {
  billingMigrationExpiresAtMs: number | null | undefined;
  locale?: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}): string | null => {
  const normalizedDeadline = normalizeBillingMigrationExpiresAtMs(billingMigrationExpiresAtMs);

  if (normalizedDeadline == null) {
    return null;
  }

  const formattedDate = new Intl.DateTimeFormat(locale || undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date(normalizedDeadline));

  const daysLeft = getSubscriptionBillingMigrationDaysLeft(normalizedDeadline);

  if (daysLeft == null || daysLeft === 0) {
    return t("subscription.legacy_migration.deadline_today", {
      date: formattedDate,
    });
  }

  return t(
    daysLeft === 1
      ? "subscription.legacy_migration.deadline_with_one_day"
      : "subscription.legacy_migration.deadline_with_days",
    {
      date: formattedDate,
      count: daysLeft,
    },
  );
};

const normalizeProductChangeDetails = (
  value: SubscriptionProductChangeDetails | undefined,
): SubscriptionProductChangeDetails => {
  if (!value) {
    return null;
  }

  return {
    new_entitlement_id: value.new_entitlement_id ?? "",
    new_product_id: value.new_product_id ?? "",
  };
};

const normalizeBillingMigrationReason = (
  value: SubscriptionBillingMigrationReason | null | undefined,
): SubscriptionBillingMigrationReason | null => {
  if (value === "legacy_without_stripe") {
    return value;
  }

  return null;
};

export const normalizeSubscriptionPlan = (raw: RawSubscriptionPlan): SubscriptionPlan => {
  const renewalAtMs = getSubscriptionRenewalAtMs({
    display_renewal_at_ms: raw.display_renewal_at_ms ?? null,
    expiration_at_ms: raw.expiration_at_ms ?? 0,
  });

  const msLeft =
    typeof raw.msLeft === "number" && Number.isFinite(raw.msLeft)
      ? Math.max(0, raw.msLeft)
      : renewalAtMs != null
        ? Math.max(0, renewalAtMs - Date.now())
        : 0;

  const daysLeft =
    typeof raw.daysLeft === "number" && Number.isFinite(raw.daysLeft)
      ? Math.max(0, raw.daysLeft)
      : msLeft > 0
        ? Math.ceil(msLeft / MS_IN_A_DAY)
        : 0;

  return {
    customer_id: raw.customer_id ?? null,
    subscription_id: raw.subscription_id ?? null,
    entitlement_id: raw.entitlement_id ?? null,
    transaction_id: raw.transaction_id ?? null,
    plan_name: raw.plan_name ?? null,
    store: raw.store?.trim() ? raw.store : null,
    starts_at_ms: raw.starts_at_ms ?? 0,
    expiration_at_ms: raw.expiration_at_ms ?? 0,
    display_renewal_at_ms: raw.display_renewal_at_ms ?? null,
    msLeft,
    daysLeft,
    product_change_details: normalizeProductChangeDetails(raw.product_change_details),
    features: {
      job_post: raw.features?.job_post ?? 0,
      shortlist: raw.features?.shortlist ?? 0,
    },
    featuresLeft: raw.featuresLeft ?? raw.features_left ?? EMPTY_FEATURES_LEFT,
    have_trial: raw.have_trial ?? false,
    is_trialing: raw.is_trialing ?? false,
    is_cancelled: raw.is_cancelled ?? false,
    was_trial_cancelled: raw.was_trial_cancelled ?? false,
    is_active: raw.is_active ?? false,
    has_stripe_subscription: raw.has_stripe_subscription ?? false,
    requires_billing_migration: raw.requires_billing_migration ?? false,
    billing_migration_reason: normalizeBillingMigrationReason(raw.billing_migration_reason),
    billing_migration_expires_at_ms: normalizeBillingMigrationExpiresAtMs(
      raw.billing_migration_expires_at_ms,
    ),
  };
};
